import { createActorContext, useSelector } from "@xstate/react";
import React, { PropsWithChildren, useEffect } from "react";
import { AlfredpayKycContext, AveniaKycContext, MoneriumKycContext, MykoboKycContext } from "../machines/kyc.states";
import { rampMachine } from "../machines/ramp.machine";
import {
  AlfredpayKycActorRef,
  AlfredpayKycSnapshot,
  AveniaKycActorRef,
  AveniaKycSnapshot,
  MoneriumKycActorRef,
  MoneriumKycSnapshot,
  MykoboKycActorRef,
  MykoboKycSnapshot,
  RampMachineSnapshot,
  SelectedAlfredpayData,
  SelectedAveniaData,
  SelectedMoneriumData,
  SelectedMykoboData
} from "../machines/types";

const RAMP_STATE_STORAGE_KEY = "rampState";

const restoredStateJSON = localStorage.getItem(RAMP_STATE_STORAGE_KEY);
let restoredState = restoredStateJSON ? JSON.parse(restoredStateJSON) : undefined;
// invalidate restored state if the machine is with error status.
restoredState = restoredState?.status === "error" ? undefined : restoredState;

export const RampStateContext = createActorContext(rampMachine, {
  snapshot: restoredState
});

export const useRampActor = RampStateContext.useActorRef;
export const useRampStateSelector = RampStateContext.useSelector;

const PersistenceEffect = () => {
  const rampActor = useRampActor();

  const moneriumActor = useSelector(rampActor, (snapshot: RampMachineSnapshot) => (snapshot.children as any).moneriumKyc) as
    | MoneriumKycActorRef
    | undefined;

  const aveniaActor = useSelector(rampActor, (snapshot: RampMachineSnapshot) => (snapshot.children as any).aveniaKyc) as
    | AveniaKycActorRef
    | undefined;

  const { rampContext, rampState, isQuoteExpired, quote } = useSelector(rampActor, state => ({
    isQuoteExpired: state?.context.isQuoteExpired,
    quote: state?.context.quote,
    rampContext: state?.context,
    rampState: state?.value
  }));

  const { moneriumState } = useSelector(moneriumActor, state => ({
    moneriumState: state?.value
  }));

  const { aveniaState } = useSelector(aveniaActor, state => ({
    aveniaState: state?.value
  }));

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const persistedSnapshot = rampActor.getPersistedSnapshot();
    localStorage.setItem("rampState", JSON.stringify(persistedSnapshot));
    // It's important to have `isQuoteExpired` and `quote` here in the deps array to persist them when they change
  }, [rampContext, rampState, moneriumState, aveniaState, isQuoteExpired, quote, rampActor.getPersistedSnapshot]);

  return null;
};

export const PersistentRampStateProvider: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <RampStateContext.Provider>
      <PersistenceEffect />
      {children}
    </RampStateContext.Provider>
  );
};

export function useMoneriumKycActor(): MoneriumKycActorRef | undefined {
  const rampActor = useRampActor();

  return useSelector(rampActor, (snapshot: RampMachineSnapshot) => (snapshot.children as any).moneriumKyc) as
    | MoneriumKycActorRef
    | undefined;
}

export function useMoneriumKycSelector(): SelectedMoneriumData | undefined {
  const rampActor = useRampActor();

  const moneriumActor = useSelector(rampActor, (snapshot: RampMachineSnapshot) => (snapshot.children as any).moneriumKyc) as
    | MoneriumKycActorRef
    | undefined;

  return useSelector(
    moneriumActor,
    (snapshot: MoneriumKycSnapshot | undefined) => {
      if (!snapshot) {
        return undefined;
      }
      return {
        context: snapshot.context as MoneriumKycContext,
        stateValue: snapshot.value
      };
    },
    (prev, next) => {
      if (!prev || !next) {
        return prev === next;
      }
      return prev.stateValue === next.stateValue && prev.context === next.context;
    }
  );
}

export function useMykoboKycActor(): MykoboKycActorRef | undefined {
  const rampActor = useRampActor();

  return useSelector(rampActor, (snapshot: RampMachineSnapshot) => (snapshot.children as any).mykoboKyc) as
    | MykoboKycActorRef
    | undefined;
}

export function useMykoboKycSelector(): SelectedMykoboData | undefined {
  const rampActor = useRampActor();

  const mykoboActor = useSelector(rampActor, (snapshot: RampMachineSnapshot) => (snapshot.children as any).mykoboKyc) as
    | MykoboKycActorRef
    | undefined;

  return useSelector(
    mykoboActor,
    (snapshot: MykoboKycSnapshot | undefined) => {
      if (!snapshot) {
        return undefined;
      }
      return {
        context: snapshot.context as MykoboKycContext,
        stateValue: snapshot.value
      };
    },
    (prev, next) => {
      if (!prev || !next) {
        return prev === next;
      }
      return prev.stateValue === next.stateValue && prev.context === next.context;
    }
  );
}

export function useAveniaKycActor(): AveniaKycActorRef | undefined {
  const rampActor = useRampActor();

  return useSelector(rampActor, (snapshot: RampMachineSnapshot) => (snapshot.children as any).aveniaKyc) as
    | AveniaKycActorRef
    | undefined;
}

export function useAveniaKycSelector(): SelectedAveniaData | undefined {
  const rampActor = useRampActor();

  const aveniaActor = useSelector(rampActor, (snapshot: RampMachineSnapshot) => (snapshot.children as any).aveniaKyc) as
    | AveniaKycActorRef
    | undefined;

  return useSelector(
    aveniaActor,
    (snapshot: AveniaKycSnapshot | undefined) => {
      if (!snapshot) {
        return undefined;
      }
      return {
        context: snapshot.context as AveniaKycContext,
        stateValue: snapshot.value
      };
    },
    (prev, next) => {
      if (!prev || !next) {
        return prev === next;
      }
      return prev.stateValue === next.stateValue && prev.context === next.context;
    }
  );
}

export function useAlfredpayKycActor(): AlfredpayKycActorRef | undefined {
  const rampActor = useRampActor();

  return useSelector(rampActor, (snapshot: RampMachineSnapshot) => (snapshot.children as any).alfredpayKyc) as
    | AlfredpayKycActorRef
    | undefined;
}

export function useAlfredpayKycSelector(): SelectedAlfredpayData | undefined {
  const rampActor = useRampActor();

  const alfredpayActor = useSelector(rampActor, (snapshot: RampMachineSnapshot) => (snapshot.children as any).alfredpayKyc) as
    | AlfredpayKycActorRef
    | undefined;

  return useSelector(
    alfredpayActor,
    (snapshot: AlfredpayKycSnapshot | undefined) => {
      if (!snapshot) {
        return undefined;
      }
      return {
        context: snapshot.context as AlfredpayKycContext,
        stateValue: snapshot.value
      };
    },
    (prev, next) => {
      if (!prev || !next) {
        return prev === next;
      }
      return prev.stateValue === next.stateValue && prev.context === next.context;
    }
  );
}
